import React, { useState, useEffect } from 'react';
import midasLogo from '../../assets/midas.png';
import coderdojoLogo from '../../assets/coderdojo.png';
import exeedLogo from '../../assets/exeed.png';
import ciscoLogo from '../../assets/cisco.png';
import nactvetLogo from '../../assets/nactvet.png';
import paloaltoLogo from '../../assets/paloalto.png';
import comptiaLogo from '../../assets/comptia.png';
import cmsApi from '../../services/cmsApi';
import { getImageUrl } from '../../utils/getImageUrl';

const PartnershipReel = () => {
  const [partnershipData, setPartnershipData] = useState({
    title: 'Our Trusted Partners',
    subtitle: 'Collaborating with industry leaders to deliver world-class education and cutting-edge technology solutions',
    partners: [
      { name: 'MIDAS', logo: midasLogo, description: 'Digital Innovation Partner' },
      { name: 'CoderDojo', logo: coderdojoLogo, description: 'Coding Education Partner' },
      { name: 'EXEED', logo: exeedLogo, description: 'Global Education Partner' },
      { name: 'Cisco', logo: ciscoLogo, description: 'Networking Technology Partner' },
      { name: 'NACTVET', logo: nactvetLogo, description: 'Vocational Training Partner' },
      { name: 'Palo Alto', logo: paloaltoLogo, description: 'Cybersecurity Partner' },
      { name: 'CompTIA', logo: comptiaLogo, description: 'IT Certification Partner' }
    ]
  });

  // Fetch partnership content from CMS (client-side only)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const fetchPartnershipContent = async () => {
      try {
        const result = await cmsApi.getHomepageComponentByType('partnership');
        if (result.success && result.components.length > 0) {
          const partnership = result.components[0];
          const settings = partnership.settings || {};
          const partners = settings.partners || [];
          
          const defaultPartners = [
            { name: 'MIDAS', logo: midasLogo, description: 'Digital Innovation Partner' },
            { name: 'CoderDojo', logo: coderdojoLogo, description: 'Coding Education Partner' },
            { name: 'EXEED', logo: exeedLogo, description: 'Global Education Partner' },
            { name: 'Cisco', logo: ciscoLogo, description: 'Networking Technology Partner' },
            { name: 'NACTVET', logo: nactvetLogo, description: 'Vocational Training Partner' },
            { name: 'Palo Alto', logo: paloaltoLogo, description: 'Cybersecurity Partner' },
            { name: 'CompTIA', logo: comptiaLogo, description: 'IT Certification Partner' }
          ];
          
          // Transform partners from CMS (with image URLs) or use defaults
          const transformedPartners = partners.length > 0 
            ? partners.map(partner => ({
                name: partner.name || '',
                logo: partner.logo || '',
                description: partner.description || ''
              }))
            : defaultPartners;
          
          setPartnershipData({
            title: partnership.title || 'Our Trusted Partners',
            subtitle: partnership.subtitle || 'Collaborating with industry leaders to deliver world-class education and cutting-edge technology solutions',
            partners: transformedPartners
          });
        }
      } catch (error) {
        console.log('Using default partnership content:', error.message);
      }
    };

    fetchPartnershipContent();
  }, []);

  const partners = partnershipData.partners;

  // Duplicate partners for seamless loop
  const duplicatedPartners = [...partners, ...partners];

  return (
    <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-r from-gray-50 to-blue-50 relative overflow-hidden min-[1024px]:max-[1535px]:!py-10">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23933eea' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Section Header */}
      <div className="container mx-auto px-4 text-center mb-8 sm:mb-12 relative z-10 min-[1024px]:max-[1535px]:!mb-6">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 min-[1024px]:max-[1535px]:!text-2xl min-[1024px]:max-[1535px]:!mb-2">
          {partnershipData.title.split(' ').map((word, index, arr) => {
            // Check if this word or the next word should be highlighted
            const isHighlighted = word.toLowerCase().includes('trusted') || 
                                 word.toLowerCase().includes('partners') ||
                                 (index > 0 && arr[index - 1].toLowerCase().includes('trusted'));
            return (
              <React.Fragment key={index}>
                {isHighlighted ? (
                  <span className="text-custom-pink">{word}</span>
                ) : (
                  word
                )}
                {index < arr.length - 1 && ' '}
              </React.Fragment>
            );
          })}
        </h2>
        <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto px-4 min-[1024px]:max-[1535px]:!text-sm">
          {partnershipData.subtitle}
        </p>
      </div>

      {/* Partnership Reel */}
      <div className="relative">
        {/* Gradient Overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 lg:w-32 bg-gradient-to-r from-gray-50 to-transparent z-10"></div>
        <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 lg:w-32 bg-gradient-to-l from-gray-50 to-transparent z-10"></div>
        
        {/* Animated Reel Container */}
        <div className="partnership-reel-container overflow-hidden">
          <div className="partnership-reel flex animate-scroll items-center">
            {duplicatedPartners.map((partner, index) => (
              <div
                key={`${partner.name}-${index}`}
                className="partnership-item flex-shrink-0 mx-4 sm:mx-6 lg:mx-8 group flex flex-col items-center"
              >
                {/* Logo */}
                <div className="w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-300 min-[1024px]:max-[1535px]:!w-14 min-[1024px]:max-[1535px]:!h-14">
                  <img
                    src={getImageUrl(partner.logo)}
                    alt={`${partner.name} Logo`}
                    className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                    onError={(e) => {
                      console.error('Failed to load partner logo:', partner.logo);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                
                {/* Partner Name */}
                <h3 className="text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-custom-pink transition-colors duration-300 text-center leading-tight">
                  {partner.name}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </div>


      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-scroll {
          animation: scroll 60s linear infinite;
        }
        
        .partnership-reel-container:hover .animate-scroll {
          animation-play-state: paused;
        }
        
        .partnership-item {
          width: 120px;
        }
        
        @media (min-width: 640px) {
          .partnership-item {
            width: 140px;
          }
        }
        
        @media (min-width: 1024px) {
          .partnership-item {
            width: 160px;
          }
        }
        
        @media (max-width: 480px) {
          .partnership-item {
            width: 100px;
          }
        }
      `}</style>
    </section>
  );
};

export default PartnershipReel;
